<script setup lang="ts">
/**
 * HomePage —— 首页长页骨架（F03）。
 *
 * F02 落地的 Showcase Store 在此处真正触发首屏拉取（`onMounted` 在
 * `idle` 态调 `store.bootstrap()`，与 store 内部并发安全的状态机协作；
 * `bootstrap` 自动从 URL `?preview=` 探测预览态，UI 横幅留 F08 接管）。
 *
 * 状态分支：
 *   idle / loading       → 骨架屏占位
 *   failed               → Critical Failure Banner 占位（F7 接管真实交互）
 *   ready / revalidating → 按 Section.type 经 Registry 解析 + 区间顺序渲染
 *
 * **区间顺序**（Q6 决策，ADR-0002 §修订记录）：
 *   `store.state.aggregate.sections` 按 sort 全局升序，但若 type 交错
 *   会渲染成 `problem→method→problem` 错乱顺序；HomePage 必须先经
 *   `groupSectionsByType` 把同 type 聚为一区，区间按 min(sort) 升序，
 *   区内按 sort 升序——禁止业务代码自行 `switch (type)` 分发或逐项
 *   遍历 `store.sections`。
 *
 * **两类 type 分发**（F12，ADR-0013）：
 *   - 卡片型（problem / method / innovation）→ `<GroupSection>` 区容器
 *     作 grid 直接子项（`id=type` 锚点 + 区标题 Icon + displayName + 内部
 *     `.scu-grid` 单卡组件，1 卡 = 1 Section）。
 *   - widget 型（impact / video / intro / gallery / material / stats /
 *     banner）→ Section 自带 `SectionCard` 壳直接作 grid item（区内首个
 *     `id=type`，其余 `section-<guid>` 保唯一），不套 HomePage 区标题。
 *
 * **CSS Grid 双列**（Q7 决策）：主容器 `auto-fit minmax(560px, 1fr)`，
 *   PC ≥1160px 自动 2 列、窄屏 <600px 自动塌成单列，零 JS、零 media
 *   query 兜底。grid 直接子项是"区容器（卡片型）或 widget 型 Section 根"；
 *   全宽 type（banner）由组件根级自带 `grid-column: 1 / -1`，HomePage
 *   不包一层 grid wrapper 遮蔽该能力。
 */
import { computed, onMounted, inject, ref, watch, markRaw } from 'vue';

import CriticalFailureBanner from '@/components/CriticalFailureBanner.vue';
import PreviewBanner from '@/components/PreviewBanner.vue';
import ResearchInstitutions from '@/components/ResearchInstitutions.vue';
import GroupSection from '@/registry/sections/GroupSection.vue';
import SectionErrorBoundary from '@/registry/sections/SectionErrorBoundary.vue';
import { useShowcase, ACTIVE_ANCHOR_ID_KEY } from '@/store/use-showcase';
import { resolveSection } from '@/registry/section-registry';
import { groupSectionsByType } from '@/composables/group-sections';
import { homeAnchorIds } from '@/composables/home-anchor-ids';
import {
  anchorIdOf,
  isCardGroupType,
  resolveGroupTitle,
} from '@/composables/group-anchor';
import { sectionGroupIcons } from '@/components/icon-registry';
import { sectionAnchorId } from '@/composables/anchor-targets';
import {
  useActiveAnchor,
  useInitialHashAnchorScroll,
} from '@/composables/use-active-anchor';
import { isModuleSectionType } from '@/composables/module-target';
import { copy } from '@/i18n/zh-CN';
import type { AggregateView, DataCardView, PublicSectionView, ResearchInstitutionView } from '@/api/types';

const store = useShowcase();
const activeAnchorId = inject(ACTIVE_ANCHOR_ID_KEY, ref(''));
const HOME_HIDDEN_SECTION_TYPES = new Set(['material']);

function isHomeVisibleSection(section: PublicSectionView): boolean {
  return !HOME_HIDDEN_SECTION_TYPES.has(section.type) && !isModuleSectionType(section.type);
}

onMounted(() => {
  if (store.state.status !== 'idle') {
    return;
  }
  void store.bootstrap();
});

const groups = computed(() =>
  store.state.aggregate !== null
    ? groupSectionsByType(
      store.state.aggregate.sections.filter(isHomeVisibleSection),
    )
    : [],
);
const dataCards = computed(() => aggregateDataCards(store.state.aggregate));

/**
 * 渲染分组：在 `groupSectionsByType` 基础上补区标题（`resolveGroupTitle`
 * 主源 NavItem.displayName + Copy Catalog 兜底）+ 区图标（`sectionGroupIcons`）。
 * **仅卡片型**调 `resolveGroupTitle`（widget 型不渲染 HomePage 区标题，
 * 自带 `SectionCard` 标题，调了反而触发无 NavItem 的 fallback warn 噪声）。
 * computed 缓存避免重渲染重复 warn。
 */
const renderGroups = computed(() =>
  groups.value.map((group) => {
    if (!isCardGroupType(group.type)) {
      return { type: group.type, sections: group.sections, title: '', icon: undefined };
    }
    return {
      type: group.type,
      sections: group.sections,
      title: resolveGroupTitle(group.type, store.state.aggregate?.navItems ?? []),
      icon: sectionGroupIcons[group.type],
    };
  }),
);

/** 每个区一个锚点 id=type（与一级 NavItem `#type` 约定对齐，F12 ADR-0013）+ 非空科研机构追加 `institutions`（F14 ADR-0015）。 */
const sectionAnchorIds = computed(() => homeAnchorIds(store.state.aggregate));

/**
 * F14 科研机构列表（SiteConfig.researchInstitutions）。空数组 → HomePage 不挂载
 * 组件（外层 v-if），组件内部再 v-if 兜底直挂测试场景。非 Section，不走 Registry。
 */
const researchInstitutions = computed<readonly ResearchInstitutionView[]>(
  () => store.state.aggregate?.siteConfig.researchInstitutions ?? [],
);

const { activeId } = useActiveAnchor(sectionAnchorIds);
useInitialHashAnchorScroll(sectionAnchorIds);

/**
 * 把首页 IntersectionObserver 的 activeId 同步到全局共享 ref（F13）。
 * App.vue 的 `<AnchorNav>` 读取该 ref 驱动首页区高亮。observer 留在
 * HomePage 以保证 home→detail→home 跨路由重挂载时观察器正确重建。
 */
watch(activeId, (value) => {
  activeAnchorId.value = value;
}, { immediate: true });

const isLoading = computed(
  () => store.state.status === 'idle' || store.state.status === 'loading',
);

const previewToken = computed(() => store.state.previewToken);

function aggregateDataCards(aggregate: AggregateView | null): readonly DataCardView[] {
  if (aggregate === null) {
    return [];
  }
  if (Array.isArray(aggregate.dataCards)) {
    return aggregate.dataCards;
  }
  // eslint-disable-next-line no-console
  console.error('[Aggregate] invalid dataCards');
  return [];
}

/**
 * DataCard 是 aggregate 顶层数字区，不属于单个 Section；多个 impact
 * Section 只让首个组件渲染数字面板，避免同一组数据重复出现。
 */
function dataCardsForSection(
  type: string,
  sections: readonly PublicSectionView[],
  section: PublicSectionView,
): readonly DataCardView[] | undefined {
  if (type !== 'impact') {
    return undefined;
  }
  return sections[0]?.id === section.id ? dataCards.value : [];
}

/**
 * 预览模式下 Draft Section 挂草稿角标（F08）。普通访客态 isPreview=false
 * 恒返 false；公共聚合本就只含 Published，双保险。section 组件不直接读
 * store，由 HomePage 统一计算后经 SectionErrorBoundary 透传。
 */
function showDraftBadgeFor(section: PublicSectionView): boolean {
  return store.state.isPreview && section.publishStatus === 'Draft';
}

/**
 * widget 型区锚点 id 规则：区内首个 Section 挂 `type` id 供 AnchorNav 命中，
 * 其余 Section 用 `section-<guid>` 保持 HTML id 唯一（多 impact 等边界）。
 * 卡片型区锚点在 `<GroupSection>` 根，卡片自身不挂 id。
 */
function widgetSectionId(
  type: string,
  sections: readonly PublicSectionView[],
  index: number,
): string {
  return index === 0 ? anchorIdOf(type) : sectionAnchorId(sections[index]!.id);
}
</script>

<template>
  <main :class="$style.page">
    <PreviewBanner v-if="store.state.isPreview" />

    <div
      v-if="isLoading"
      :class="$style.skeleton"
      data-testid="home-skeleton"
    >
      {{ copy.app.loading }}
    </div>

    <CriticalFailureBanner
      v-else-if="store.state.status === 'failed'"
      :error="store.state.error"
    />

    <div
      v-else
      :class="$style.grid"
      data-testid="home-grid"
    >
      <template v-for="group in renderGroups" :key="group.type">
        <template v-if="isCardGroupType(group.type)">
          <GroupSection
            :id="group.type"
            :type="group.type"
            :title="group.title"
            :icon="group.icon"
            :more-section="group.sections[0]"
            :preview-token="previewToken"
          >
            <SectionErrorBoundary
              v-for="section in group.sections"
              :key="section.id"
              :component="markRaw(resolveSection(section.type))"
              :section="section"
              :show-draft-badge="showDraftBadgeFor(section)"
              :preview-token="previewToken"
            />
          </GroupSection>
        </template>
        <template v-else>
          <SectionErrorBoundary
            v-for="(section, index) in group.sections"
            :key="section.id"
            :component="markRaw(resolveSection(section.type))"
            :section="section"
            :id="widgetSectionId(group.type, group.sections, index)"
            :data-cards="dataCardsForSection(group.type, group.sections, section)"
            :show-draft-badge="showDraftBadgeFor(section)"
            :preview-token="previewToken"
          />
        </template>
      </template>

      <!-- F14 科研机构全宽区：SiteConfig 驱动，非 Section，置于 grid 末尾占满整行。 -->
      <ResearchInstitutions
        v-if="researchInstitutions.length > 0"
        :institutions="researchInstitutions"
      />
    </div>
  </main>
</template>

<style module>
.page {
  padding: var(--template-home-padding-top) 0 var(--template-home-padding-bottom);
}

.grid {
  width: min(var(--container-max), calc(100% - var(--space-4)));
  max-width: var(--container-max);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--template-home-gap);
}

.grid > * {
  scroll-margin-top: var(--space-6);
}

.skeleton {
  max-width: var(--container-max);
  margin: 0 auto;
  padding: var(--space-5) var(--space-3);
  background: var(--color-bg-secondary);
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  font-size: var(--font-size-md);
  text-align: center;
}

@media (max-width: 1199px) {
  .grid {
    grid-template-columns: 1fr;
  }
}
</style>
